import React, { useState, useEffect, useRef } from 'react';
import { Settings, Package, LogOut, User, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Home, BookOpen, List, BarChart, Receipt, Tag, MessageCircleIcon, MessageCircle, AlertCircle, ScrollText, UserCircle2, ShoppingCartIcon, Video, Subtitles, Activity, Package2Icon, BadgePercent, Box, Truck, TimerReset, MessageSquare, Search, CircleDot, Smartphone } from 'lucide-react';
import './Sidebar.css';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { authAPI } from '../../config/auth-api';
import { toast } from 'react-toastify';

const Sidebar = ({ onLogout, currentPage, onPageChange, adminData, setAdminData }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [wasCollapsedBeforeExpand, setWasCollapsedBeforeExpand] = useState(false);
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const storeDropdownRef = useRef(null);
  const [isSwitchingStore, setIsSwitchingStore] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        storeDropdownRef.current &&
        !storeDropdownRef.current.contains(event.target)
      ) {
        setIsStoreDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canViewMenu = (menuId) => {
    const permissionMap = {
      'event-logs': ADMIN_PERMISSIONS.VIEW_EVENT_LOGS,
      'server-errors': ADMIN_PERMISSIONS.VIEW_SERVER_ERRORS,
      'stories': ADMIN_PERMISSIONS.VIEW_STORIES,
      'banner-products': ADMIN_PERMISSIONS.VIEW_BANNER_PRODUCTS,
      'reel-videos': ADMIN_PERMISSIONS.VIEW_REEL_VIDEOS,
      'product-list': ADMIN_PERMISSIONS.VIEW_PRODUCTS,
      'categories': ADMIN_PERMISSIONS.VIEW_CATEGORIES,
      'user-order-report': ADMIN_PERMISSIONS.VIEW_REPORTS,
      'category-report': ADMIN_PERMISSIONS.VIEW_REPORTS,
      'coupon': ADMIN_PERMISSIONS.VIEW_COUPON,
      'whatsapp': ADMIN_PERMISSIONS.VIEW_WHATSAPP_QUE,
      'whatsapp-chat': ADMIN_PERMISSIONS.VIEW_WHATSAPP_CHAT,
      'crm-summary': ADMIN_PERMISSIONS.VIEW_CRM_SUMMARY,
      'notification': ADMIN_PERMISSIONS.VIEW_NOTIFICATION_QUE,
      'analytics': ADMIN_PERMISSIONS.VIEW_ANALYTICS,
      'admins': ADMIN_PERMISSIONS.VIEW_ADMINS,
      'users': ADMIN_PERMISSIONS.VIEW_USERS,
      'orders': ADMIN_PERMISSIONS.VIEW_ORDERS,
      'dispatch-planner': ADMIN_PERMISSIONS.VIEW_DISPATCH_PLANNER,
      'delivery-staff': ADMIN_PERMISSIONS.VIEW_DELIVERY_STAFF,
      'packing-staff': ADMIN_PERMISSIONS.VIEW_PACKING_STAFF,
      'admin-app': ADMIN_PERMISSIONS.VIEW_ADMIN_APP,
      'dispatch-history': ADMIN_PERMISSIONS.VIEW_DISPATCH_HISTORY,
      'settings': ADMIN_PERMISSIONS.VIEW_SETTINGS,
      'chat-support': ADMIN_PERMISSIONS.VIEW_CHAT_SUPPORT,
      'seo': ADMIN_PERMISSIONS.VIEW_SEO,
      'spin-wheel': ADMIN_PERMISSIONS.VIEW_SPIN_WHEEL,
    };

    if (menuId === 'orders') {
      return helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS) ||
        helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_CUSTOM_ORDERS);
    }

    return permissionMap[menuId] ? helpers.hasPermission(adminData, permissionMap[menuId]) : true;
  };

  const menuItems = [
    {
      id: 'welcome',
      label: 'Dashboard',
      icon: Home,
      hasSubmenu: true,
      submenu: [
        {
          id: 'event-logs',
          label: 'Admin Logs',
          icon: ScrollText,
          page: 'event-logs',
          permissionKey: 'event-logs'
        },
        {
          id: 'server-errors',
          label: 'Server Errors',
          icon: AlertCircle,
          page: 'server-errors',
          permissionKey: 'server-errors'
        },
        {
          id: 'app-tester',
          label: 'App Tester',
          icon: Smartphone,
          page: 'app-tester'
        },
      ]
    },
    {
      id: 'app-homepage',
      label: 'App Homepage',
      icon: BookOpen,
      hasSubmenu: true,
      submenu: [
        {
          id: 'stories',
          label: 'Stories',
          icon: BookOpen,
          page: 'stories',
          permissionKey: 'stories'
        },
        {
          id: 'banner-products',
          label: 'Banners & Sections',
          icon: Tag,
          page: 'banner-products',
          permissionKey: 'banner-products'
        },
        {
          id: 'reel-videos',
          label: 'Reel Videos',
          icon: Video,
          page: 'reel-videos',
          permissionKey: 'reel-videos'
        }
      ]
    },
    {
      id: 'seo',
      label: 'SEO',
      icon: Search,
      page: 'seo',
      permissionKey: 'seo'
    },
    {
      id: 'spin-wheel',
      label: 'Spin Wheel',
      icon: CircleDot,
      page: 'spin-wheel',
      permissionKey: 'spin-wheel'
    },
    {
      id: 'products',
      label: 'Products',
      icon: Package,
      hasSubmenu: true,
      submenu: [
        {
          id: 'product-list',
          label: 'List',
          icon: List,
          page: 'product-list',
          permissionKey: 'product-list'
        },
        {
          id: 'categories',
          label: 'Categories',
          icon: Tag,
          page: 'categories',
          permissionKey: 'categories'
        }
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart,
      hasSubmenu: true,
      submenu: [
        {
          id: 'user-order-report',
          label: 'User Order Report',
          icon: Receipt,
          page: 'user-order-report',
          permissionKey: 'user-order-report'
        },
        {
          id: 'category-report',
          label: 'Category Wise Report',
          icon: List,
          page: 'category-report',
          permissionKey: 'category-report'
        }
      ]
    },
    {
      id: 'coupon',
      label: 'Coupon',
      icon: BadgePercent,
      page: 'coupon',
      permissionKey: 'coupon'
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: MessageCircle,
      hasSubmenu: true,
      submenu: [
        {
          id: 'crm-summary',
          label: 'Summary',
          icon: Subtitles,
          page: 'crm-summary',
          permissionKey: 'crm-summary'
        },
        {
          id: 'whatsapp-que',
          label: 'Whatsapp Que',
          icon: MessageCircleIcon,
          page: 'whatsapp',
          permissionKey: 'whatsapp'
        },
        {
          id: 'notification-que',
          label: 'Notification Que',
          icon: AlertCircle,
          page: 'notification',
          permissionKey: 'notification'
        }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart,
      hasSubmenu: true,
      submenu: [
        {
          id: 'user-analytics',
          label: 'User',
          icon: Activity,
          page: 'analytics',
          permissionKey: 'analytics'
        },
      ]
    },
    {
      id: 'staffs',
      label: 'Staff',
      icon: User,
      hasSubmenu: true,
      submenu: [
        {
          id: 'admins',
          label: 'Admins',
          icon: UserCircle2,
          page: 'admins',
          permissionKey: 'admins'
        },
        {
          id: 'delivery-staff',
          label: 'Delivery Staff',
          icon: Truck,
          page: 'delivery-staff',
          permissionKey: 'delivery-staff'
        },
        {
          id: 'packing-staff',
          label: 'Packing Staff',
          icon: Box,
          page: 'packing-staff',
          permissionKey: 'packing-staff'
        },
        {
          id: 'admin-app',
          label: 'Admin App',
          icon: Smartphone,
          page: 'admin-app',
          permissionKey: 'admin-app'
        }
      ]
    },
    {
      id: 'users',
      label: 'Users',
      icon: UserCircle2,
      page: 'users',
      permissionKey: 'users'
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCartIcon,
      page: 'orders',
      permissionKey: 'orders'
    },
    {
      id: 'dispatch',
      label: 'Dispatch',
      icon: Box,
      page: 'dispatch-planner',
      permissionKey: 'dispatch-planner'
    },
    {
      id: 'whatsapp-chat',
      label: 'WhatsApp Chat',
      icon: MessageSquare,
      page: 'whatsapp-chat',
      permissionKey: 'whatsapp-chat'
    },
    {
      id: 'chat-support',
      label: 'Chat Support',
      icon: MessageSquare,
      page: 'chat-support',
      permissionKey: 'chat-support'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      page: 'settings',
      permissionKey: 'settings'
    },
  ];

  const appName = process.env.REACT_APP_APP_NAME || '';

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    if (wasCollapsedBeforeExpand) {
      setWasCollapsedBeforeExpand(false);
      setExpandedMenus({});
    }
  };

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
      if (isCollapsed && !isMobile) {
        setWasCollapsedBeforeExpand(true);
        setIsCollapsed(false);
        setTimeout(() => {
          toggleSubmenu(item.id);
        }, 300);
      } else {
        toggleSubmenu(item.id);
      }
    } else {
      onPageChange(item.page);
      if (isMobile) {
        setIsCollapsed(true);
      }
    }
  };

  const handleSubmenuClick = (submenuItem) => {
    onPageChange(submenuItem.page);

    if (isMobile) {
      setIsCollapsed(true);
      setExpandedMenus({});
    } else if (wasCollapsedBeforeExpand) {
      setTimeout(() => {
        setIsCollapsed(true);
        setWasCollapsedBeforeExpand(false);
        setExpandedMenus({});
      }, 100);
    }
  };

  const handleStoreSwitch = async (store) => {
    if (store.storeId.toString() === adminData.currentStore.storeId.toString()) {
      return;
    }

    setIsSwitchingStore(true);
    setIsStoreDropdownOpen(false);

    try {
      const result = await authAPI.switchStore(store.storeId);
      if (result.success) {
        toast.success('Store switched successfully');
        setAdminData(prev => ({
          ...prev,
          currentStore: result.data.currentStore,
          stores: result.data.stores,
          accessToken: result.data.accessToken
        }));
        // window.location.reload();
        onPageChange('welcome');
      } else {
        toast.error(result.error || 'Failed to switch store');
      }
    } catch (error) {
      toast.error('Failed to switch store');
    } finally {
      setIsSwitchingStore(false);
    }
  }

  return (
    <>
      {isMobile && isCollapsed && (
        <button
          className="sidebar-mobile-menu-button"
          onClick={toggleSidebar}
        >
          <ChevronRight size={20} />
        </button>
      )}

      <div className={`sidebar-container ${isCollapsed ? 'sidebar-collapsed' : ''} ${wasCollapsedBeforeExpand ? 'sidebar-temporary-expanded' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          {!isCollapsed && (
            <div className="sidebar-logo-section">
              <div className="sidebar-logo-icon">A</div>
              <div className="sidebar-logo-text">
                <h2>{appName}</h2>
                <span className="sidebar-logo-subtitle">Dashboard</span>
              </div>
            </div>
          )}

          {isCollapsed && (
            <div className="sidebar-logo-collapsed">
              <div className="sidebar-logo-icon-small">A</div>
            </div>
          )}

          <button
            className="sidebar-toggle-button"
            onClick={toggleSidebar}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Admin Info */}
        <div className="sidebar-admin-info">
          {!isCollapsed && (
            <>
              <div className="sidebar-admin-avatar">
                <User size={24} />
              </div>
              <div className="sidebar-admin-details">
                <h3>{adminData?.name || ''}</h3>

                {adminData?.currentStore && (
                  <div className="sidebar-store-dropdown" ref={storeDropdownRef}>
                    <button
                      className="sidebar-store-dropdown-toggle"
                      onClick={() => setIsStoreDropdownOpen(prev => !prev)}
                      disabled={isSwitchingStore}
                    >
                      <span>{isSwitchingStore ? 'Switching...' : adminData.currentStore.storeName}</span>
                      {!isSwitchingStore && adminData?.stores?.length > 1 && <ChevronDown size={14} />}
                    </button>

                    {isStoreDropdownOpen && (
                      <div className="sidebar-store-dropdown-menu">
                        {adminData.stores.map((store) => (
                          <button
                            key={store.storeId}
                            disabled={isSwitchingStore}
                            className={`sidebar-store-dropdown-item ${store.storeId.toString() === adminData.currentStore.storeId.toString() ? 'sidebar-store-dropdown-item-active' : ''}`}
                            onClick={() => handleStoreSwitch(store)}
                          >
                            <span>{store.storeName}</span>
                            {store.storeId === adminData.currentStore.storeId && (
                              <span className="sidebar-store-current-indicator">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <span>{adminData?.email || ''}</span>
              </div>
            </>
          )}

          {isCollapsed && (
            <div className="sidebar-admin-avatar-collapsed">
              <User size={20} />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {adminData?.smsBalance !== undefined && adminData?.smsBalance !== null && (
            <div 
              className={`sidebar-sms-balance-card ${isCollapsed ? 'collapsed' : ''}`} 
              title={`SMS Balance: ${typeof adminData.smsBalance === 'number' ? adminData.smsBalance.toFixed(2) : adminData.smsBalance}`}
            >
              <div className="sms-balance-icon-container">
                <MessageSquare size={isCollapsed ? 18 : 14} />
              </div>
              {!isCollapsed && (
                <div className="sms-balance-info">
                  <span className="sms-balance-label">SMS Balance</span>
                  <span className="sms-balance-value">
                    {typeof adminData.smsBalance === 'number' ? adminData.smsBalance.toFixed(2) : adminData.smsBalance}
                  </span>
                </div>
              )}
            </div>
          )}

          {menuItems.map((item) => {
            const visibleSubmenu = item.hasSubmenu
              ? item.submenu.filter(sub => !sub.permissionKey || canViewMenu(sub.permissionKey))
              : null;

            if (item.hasSubmenu && visibleSubmenu.length === 0) return null;
            if (item.permissionKey && !canViewMenu(item.permissionKey)) return null;

            const Icon = item.icon;
            const isExpanded = expandedMenus[item.id];
            const isActive = (item.hasSubmenu && item.submenu.some(sub => currentPage === sub.page)) || (!item.hasSubmenu && currentPage === item.page);

            return (
              <div key={item.id} className="sidebar-nav-item-container">
                <button
                  className={`sidebar-nav-item ${isActive ? 'sidebar-nav-item-active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon size={20} />
                  {!isCollapsed && <span>{item.label}</span>}
                  {!isCollapsed && item.hasSubmenu && (
                    <span className="sidebar-submenu-arrow">
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </span>
                  )}
                </button>

                {!isCollapsed && item.hasSubmenu && isExpanded && (
                  <div className="sidebar-submenu">
                    {visibleSubmenu.map((submenuItem) => {
                      const SubIcon = submenuItem.icon;
                      return (
                        <button
                          key={submenuItem.id}
                          className={`sidebar-submenu-item ${currentPage === submenuItem.page ? 'sidebar-submenu-item-active' : ''}`}
                          onClick={() => handleSubmenuClick(submenuItem)}
                        >
                          <SubIcon size={16} />
                          <span>{submenuItem.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <button
            className="sidebar-logout-button"
            onClick={() => setShowLogoutConfirm(true)}
            title={isCollapsed ? 'Logout' : ''}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
      {showLogoutConfirm && (
        <LogoutConfirmDialog
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </>
  );
};

const LogoutConfirmDialog = ({ onConfirm, onCancel }) => {
  return (
    <div className="sidebar-confirm-overlay" onClick={onCancel}>
      <div className="sidebar-confirm-modal simple" onClick={e => e.stopPropagation()}>
        <h3>Log Out?</h3>
        <p>Are you sure you want to log out?</p>
        <div className="sidebar-confirm-actions">
          <button className="sidebar-confirm-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="sidebar-confirm-btn-logout" onClick={onConfirm}>
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;